import axios from 'axios';

// Prefer same-origin for API calls when the app is served from a custom domain.
// This keeps auth cookies same-site and avoids CORS when the user visits
// the custom domain (e.g. bop.wellconnected.net.au) instead of the
// build-time REACT_APP_BACKEND_URL (e.g. the emergent.host URL).
const ENV_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL =
  typeof window !== 'undefined' && window.location && window.location.origin && window.location.origin !== ENV_URL
    ? window.location.origin
    : ENV_URL;

export { API_URL };

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/api/auth/me')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/quote/acceptance')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/api/auth/login', data);
export const register = (data) => api.post('/api/auth/register', data);
export const getMe = () => api.get('/api/auth/me');
export const apiLogout = () => api.post('/api/auth/logout');

// Users
export const getUsers = (params) => api.get('/api/users', { params });
export const getUser = (id) => api.get(`/api/users/${id}`);
export const updateUser = (id, data) => api.put(`/api/users/${id}`, data);
export const changeUserPassword = (id, data) => api.put(`/api/users/${id}/password`, data);
export const updateUserStatus = (id, data) => api.put(`/api/users/${id}/status`, data);
export const toggleDashboardVisibility = (id, data) => api.put(`/api/users/${id}/dashboard-toggle`, data);
export const getDashboardVisibleUsers = () => api.get('/api/users/dashboard-visible');
export const removeUser = (id) => api.delete(`/api/users/${id}`);
export const uploadUserPhoto = (id, formData) => api.post(`/api/users/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getMyProfile = () => api.get('/api/auth/profile');
export const updateMyProfile = (data) => api.put('/api/auth/profile', data);
export const changeMyPassword = (data) => api.put('/api/auth/password', data);
export const getInhouseDropdown = () => api.get('/api/users/dropdown/inhouse');
export const getAllUsersDropdown = () => api.get('/api/users/dropdown/all');
export const getWorkersDropdown = () => api.get('/api/users/dropdown/workers');

// Clients
export const getClients = (params) => api.get('/api/clients', { params });
export const getClient = (id) => api.get(`/api/clients/${id}`);
export const createClient = (data) => api.post('/api/clients', data);
export const updateClient = (id, data) => api.put(`/api/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/api/clients/${id}`);
export const getClientsDropdown = () => api.get('/api/dropdowns/clients');

// Property Addresses
export const getPropertyAddresses = (params) => api.get('/api/property-addresses', { params });
export const getPropertyAddress = (id) => api.get(`/api/property-addresses/${id}`);
export const createPropertyAddress = (data) => api.post('/api/property-addresses', data);
export const updatePropertyAddress = (id, data) => api.put(`/api/property-addresses/${id}`, data);
export const deletePropertyAddress = (id) => api.delete(`/api/property-addresses/${id}`);

// Property Managers
export const getPropertyManagers = (params) => api.get('/api/property-managers', { params });
export const createPropertyManager = (data) => api.post('/api/property-managers', data);
export const updatePropertyManager = (id, data) => api.put(`/api/property-managers/${id}`, data);

// Quotes
export const getQuotes = (params) => api.get('/api/quotes', { params });
export const getQuote = (id) => api.get(`/api/quotes/${id}`);
export const createQuote = (data) => api.post('/api/quotes', data);
export const updateQuote = (id, data) => api.put(`/api/quotes/${id}`, data);
export const updateQuoteStatus = (id, data) => api.put(`/api/quotes/${id}/status`, typeof data === 'object' ? data : { status: data });
export const reviseQuote = (id, data) => api.post(`/api/quotes/${id}/revise`, data);
export const deleteQuote = (id) => api.delete(`/api/quotes/${id}`);
export const assignQuote = (id, data) => api.patch(`/api/quotes/${id}/assign`, data);

// Quote Descriptions
export const createQuoteDescription = (data) => api.post('/api/quote-descriptions', data);
export const updateQuoteDescription = (id, data) => api.put(`/api/quote-descriptions/${id}`, data);
export const deleteQuoteDescription = (id) => api.delete(`/api/quote-descriptions/${id}`);

// Quote Items
export const createQuoteItem = (data) => api.post('/api/quote-items', data);
export const updateQuoteItem = (id, data) => api.put(`/api/quote-items/${id}`, data);
export const deleteQuoteItem = (id) => api.delete(`/api/quote-items/${id}`);

// Jobs
export const getJobs = (params) => api.get('/api/jobs', { params });
export const getJob = (id) => api.get(`/api/jobs/${id}`);
export const createJob = (data) => api.post('/api/jobs', data);
export const addDescriptionsToJob = (jobId, data) => api.post(`/api/jobs/${jobId}/add-descriptions`, data);
export const updateJob = (id, data) => api.put(`/api/jobs/${id}`, data);
export const updateJobStatus = (id, data) => api.put(`/api/jobs/${id}/status`, data);
export const deleteJob = (id) => api.delete(`/api/jobs/${id}`);
export const assignJob = (id, data) => api.patch(`/api/jobs/${id}/assign`, data);
export const createJobManual = (data) => api.post('/api/jobs/manual', data);

// Job Descriptions
export const createJobDescription = (data) => api.post('/api/job-descriptions', data);
export const updateJobDescription = (id, data) => api.put(`/api/job-descriptions/${id}`, data);
export const updateJobDescriptionStatus = (id, data) => api.put(`/api/job-descriptions/${id}/status`, data);
export const bookJobDescription = (id, data) => api.post(`/api/job-descriptions/${id}/book`, data);

// Job Items
export const createJobItem = (data) => api.post('/api/job-items', data);
export const updateJobItem = (id, data) => api.put(`/api/job-items/${id}`, data);
export const deleteJobItem = (id) => api.delete(`/api/job-items/${id}`);

// Job Variance
export const getJobVariance = (id) => api.get(`/api/jobs/${id}/variance`);

// Unquoted Items
export const getUnquotedItems = (jobId, params) => api.get(`/api/jobs/${jobId}/unquoted-items`, { params });
export const createUnquotedItem = (data) => api.post('/api/unquoted-items', data);
export const updateUnquotedItem = (id, data) => api.put(`/api/unquoted-items/${id}`, data);
export const deleteUnquotedItem = (id) => api.delete(`/api/unquoted-items/${id}`);

// Invoices
export const getInvoices = (params) => api.get('/api/invoices', { params });
export const getInvoice = (id) => api.get(`/api/invoices/${id}`);
export const createInvoice = (data) => api.post('/api/invoices', data);
export const updateInvoice = (id, data) => api.put(`/api/invoices/${id}`, data);
export const updateInvoiceStatus = (id, data) => api.put(`/api/invoices/${id}/status`, data);
export const deleteInvoice = (id) => api.delete(`/api/invoices/${id}`);
export const assignInvoice = (id, data) => api.patch(`/api/invoices/${id}/assign`, data);
export const submitInvoice = (id) => api.put(`/api/invoices/${id}/submit`);
export const markInvoicePaid = (id) => api.put(`/api/invoices/${id}/mark-paid`);
export const getJobInvoicePreview = (jobId) => api.get(`/api/jobs/${jobId}/invoice-preview`);
export const mergeInvoices = (data) => api.post('/api/invoices/merge', data);

// Invoice Descriptions
export const createInvoiceDescription = (data) => api.post('/api/invoice-descriptions', data);
export const getInvoiceDescription = (id) => api.get(`/api/invoice-descriptions/${id}`);
export const updateInvoiceDescription = (id, data) => api.put(`/api/invoice-descriptions/${id}`, data);
export const deleteInvoiceDescription = (id) => api.delete(`/api/invoice-descriptions/${id}`);

// Items & Services
export const getItemsServices = (params) => api.get('/api/items-services', { params });
export const getItemService = (id) => api.get(`/api/items-services/${id}`);
export const createItemService = (data) => api.post('/api/items-services', data);
export const updateItemService = (id, data) => api.put(`/api/items-services/${id}`, data);
export const deleteItemService = (id) => api.delete(`/api/items-services/${id}`);

// Labour
export const getLabour = () => api.get('/api/labour');
export const createLabour = (data) => api.post('/api/labour', data);
export const updateLabour = (id, data) => api.put(`/api/labour/${id}`, data);
export const deleteLabour = (id) => api.delete(`/api/labour/${id}`);

// Referrals
export const getReferrals = () => api.get('/api/referrals');

// Staff
export const getStaff = (params) => api.get('/api/staff', { params });

// Quote Description Suggestions (typeahead)
export const getQuoteDescriptionSuggestions = (q) => api.get('/api/quote-descriptions/suggestions', { params: { q } });
export const getQuoteDescriptionById = (id) => api.get(`/api/quote-descriptions/${id}`);

// Attachments
export const uploadAttachment = (formData) => api.post('/api/attachments/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
export const getAttachmentsByEntity = (entityType, entityId) => api.get(`/api/attachments/by-entity/${entityType}/${entityId}`);
export const downloadAttachment = (id) => api.get(`/api/attachments/${id}/download`, { responseType: 'blob' });
export const downloadThumbnail = (id) => api.get(`/api/attachments/${id}/thumbnail`, { responseType: 'blob' });
export const deleteAttachment = (id) => api.delete(`/api/attachments/${id}`);
export const downloadJobSheetWord = (jobId) => api.get(`/api/jobs/${jobId}/jobsheet-word`, { responseType: 'blob' });
export const downloadQuotePDF = (quoteId) => api.get(`/api/quotes/${quoteId}/pdf`, { responseType: 'blob' });
export const downloadInvoicePDF = (invoiceId) => api.get(`/api/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
export const saveJobVariance = (jobId, data) => api.post(`/api/jobs/${jobId}/variance/save`, data);
export const createReferral = (data) => api.post('/api/referrals', data);
export const updateReferral = (id, data) => api.put(`/api/referrals/${id}`, data);
export const deleteReferral = (id) => api.delete(`/api/referrals/${id}`);

// Comments
export const getComments = (entityType, entityId) => api.get('/api/comments', { params: { entity_type: entityType, entity_id: entityId } });
export const createComment = (data) => api.post('/api/comments', data);
export const updateComment = (id, comment) => api.put(`/api/comments/${id}`, { comment });
export const deleteComment = (id) => api.delete(`/api/comments/${id}`);

// Schedules
export const getSchedules = (params) => api.get('/api/schedules', { params });
export const createSchedule = (data) => api.post('/api/schedules', data);
export const updateSchedule = (id, data) => api.put(`/api/schedules/${id}`, data);
export const setScheduleCompleted = (id, completed) => api.put(`/api/schedules/${id}/complete`, { completed });
export const deleteSchedule = (id) => api.delete(`/api/schedules/${id}`);
export const duplicateScheduleForAssignees = (id, assignee_ids) =>
  api.post(`/api/schedules/${id}/duplicate-for-assignees`, { assignee_ids });
export const scheduleSiteVisit = (quoteId, data) => api.post(`/api/quotes/${quoteId}/site-visit`, data);
export const getSiteVisits = (quoteId) => api.get(`/api/quotes/${quoteId}/site-visits`);

// Calendar Misc Items
export const createCalendarMiscItem = (data) => api.post('/api/calendar-misc-items', data);
export const createMiscSchedule = (data) => api.post('/api/schedules/misc', data);

// Dashboard
export const getDashboardStats = () => api.get('/api/reports/dashboard');

// Calendar
export const getCalendarEvents = (params) => api.get('/api/calendar/events', { params });
export const getScheduleDetail = (id) => api.get(`/api/calendar/events/${id}`);
export const getLogoBase64 = () => api.get('/api/logo-base64');

// Reports
export const getClientsReport = (params) => api.get('/api/reports/clients', { params });
export const getQuotesReport = (params) => api.get('/api/reports/quotes', { params });
export const getJobsReport = (params) => api.get('/api/reports/jobs', { params });
export const getInvoicesReport = (params) => api.get('/api/reports/invoices', { params });

// Data Migration
export const importData = (data) => api.post('/api/migrate/import', data);

// Email Settings
export const getEmailSettings = () => api.get('/api/email-settings');
export const updateEmailSettings = (section, data) => api.put(`/api/email-settings/${section}`, data);
export const getEmailLog = (limit = 50) => api.get('/api/email-log', { params: { limit } });
export const uploadEmailBanner = (formData) => api.post('/api/email-settings/signature/upload-banner', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Email Sending
export const getQuoteEmailPreview = (quoteId) => api.get(`/api/quotes/${quoteId}/email-preview`);
export const sendQuoteEmail = (quoteId, data) => api.post(`/api/quotes/${quoteId}/send-email`, data);
export const getInvoiceEmailPreview = (invoiceId) => api.get(`/api/invoices/${invoiceId}/email-preview`);
export const sendInvoiceEmail = (invoiceId, data) => api.post(`/api/invoices/${invoiceId}/send-email`, data);
export const sendQuoteNotification = (quoteId, data) => api.post(`/api/quotes/${quoteId}/send-notification`, data);

// Quote Acceptance
export const getQuoteAcceptanceLink = (quoteId) => api.get(`/api/quotes/${quoteId}/acceptance-link`);

// Quote Note Templates
export const getQuoteNoteTemplates = () => api.get('/api/quote-note-templates');
export const createQuoteNoteTemplate = (data) => api.post('/api/quote-note-templates', data);
export const updateQuoteNoteTemplate = (id, data) => api.put(`/api/quote-note-templates/${id}`, data);
export const deleteQuoteNoteTemplate = (id) => api.delete(`/api/quote-note-templates/${id}`);

export default api;
